/**
 * The world in neon: dark blocks with a lit top edge (cyan for the ground, pink for a box, paper
 * for a pipe) and a faint grid on their faces, deep water with a glowing surface, yellow bars
 * hanging from cyan posts, a green start post and a checkered finish post with pixel-type labels.
 * Everything is drawn once from the course's world when the renderer is built; the water's
 * surface pulses and the salmon ladder's held bar follows its animated grip. The race behind the
 * title draws its course with this renderer too,
 * so the game and its title screen are one world.
 */

import { SolidClass, type World } from "@lache/sim";
import { BitmapText, Container, type Sprite } from "pixi.js";
import type { RenderFeel } from "./feel";
import { NEON, PIXEL_FONT } from "./palette";
import type { ShapeFactory } from "./shapes";

/** Drawn bar radius and the height of the post it hangs from, m. */
const BAR_RADIUS = 0.09;
const BAR_POST_HEIGHT = 3;
/** A salmon ladder: the posts this far either side of the pegs' line, the pegs this long, the posts this far past the rungs. */
const LADDER_HALF_WIDTH = 0.42;
const LADDER_PEG = 0.16;
const LADDER_POST_OVER = 0.35;
/** Shared by the course's bar and the translucent bar carried by another player's ghost. */
export const LADDER_BAR_WIDTH = 2 * LADDER_HALF_WIDTH + 2 * BAR_RADIUS;
export const LADDER_BAR_HEIGHT = 2 * BAR_RADIUS;
/** Height of the start and finish posts above the ground, m, and the checker square of the finish. */
const LINE_POST_HEIGHT = 3.4;
const FINISH_SQUARE = 0.2;
/** Height of the START and FINISH labels, m, and the size the pixel font is rendered at. */
const LINE_LABEL_HEIGHT = 0.36;
const LINE_LABEL_FONT_PX = 64;
/** Thickness of the grid lines on the faces of the blocks and of the side edges, m. */
const GRID_LINE = 0.022;
const SIDE_EDGE = 0.035;

export interface WorldRendererOptions {
  /** Draw the start and finish posts of a timed course (default true). */
  lines?: boolean;
}

export class WorldRenderer {
  readonly root = new Container();
  /** The water's surface lines, pulsing together. */
  private readonly surfaces: Sprite[] = [];
  /** Each salmon ladder's movable bar (the halo and the disc), following the interpolated grip. */
  private readonly ladderBars: { halo: Sprite; disc: Sprite }[] = [];
  private readonly world: World;

  constructor(f: ShapeFactory, world: World, feel: RenderFeel, options: WorldRendererOptions = {}) {
    this.world = world;
    const r = this.root;
    const grid = feel.gridStep > 0.1 ? feel.gridStep : 0;
    for (let i = 0; i < world.waterCount; i++) {
      const x0 = world.waterX0[i]!;
      const y0 = world.waterY0[i]!;
      const w = world.waterX1[i]! - x0;
      const h = world.waterY1[i]! - y0;
      f.rect(r, x0, y0, w, h, NEON.water, 0.92);
      f.rect(r, x0, y0, w, h * 0.5, NEON.waterDeep, 0.6);
      // Reflections under the surface, fading down.
      f.rect(r, x0, y0 + h - 0.32, w, 0.03, NEON.waterEdge, 0.22);
      f.rect(r, x0, y0 + h - 0.58, w, 0.03, NEON.waterEdge, 0.1);
      this.surfaces.push(f.rect(r, x0, y0 + h - 0.06, w, 0.06, NEON.waterEdge, 0.9));
    }
    for (let i = 0; i < world.solidCount; i++) {
      const x0 = world.solidX0[i]!;
      const y0 = world.solidY0[i]!;
      const x1 = world.solidX1[i]!;
      const y1 = world.solidY1[i]!;
      const w = x1 - x0;
      const h = y1 - y0;
      const cls = world.solidClass[i];
      if (cls === SolidClass.Pipe) {
        f.rect(r, x0, y0, w, h, NEON.pipe, 0.95);
        f.rect(r, x0, y1 - 0.05, w, 0.05, NEON.pipeEdge, 0.7);
        f.rect(r, x0, y0, w, 0.04, NEON.ink, 0.35);
        continue;
      }
      const box = cls === SolidClass.Box;
      const fill = box ? NEON.box : NEON.ground;
      const edge = box ? NEON.boxEdge : NEON.groundEdge;
      f.rect(r, x0, y0, w, h, fill, 0.97);
      if (grid > 0) {
        for (let gx = x0 + grid; gx < x1 - grid * 0.5; gx += grid)
          f.rect(r, gx - GRID_LINE / 2, y0, GRID_LINE, h, edge, feel.gridAlpha);
        for (let gy = y1 - grid; gy > y0 + grid * 0.5; gy -= grid)
          f.rect(r, x0, gy - GRID_LINE / 2, w, GRID_LINE, edge, feel.gridAlpha);
      }
      // The side faces, thin; the top edge, lit, over a glow band that fades into the face.
      f.rect(r, x0, y0, SIDE_EDGE, h, edge, 0.55);
      f.rect(r, x1 - SIDE_EDGE, y0, SIDE_EDGE, h, edge, 0.55);
      const glow = Math.min(feel.edgeGlow, h * 0.5);
      f.rect(r, x0, y1 - glow, w, glow, edge, 0.2);
      const ew = Math.min(feel.edgeWidth, h * 0.5);
      f.rect(r, x0, y1 - ew, w, ew, edge, 0.95);
    }
    for (let i = 0; i < world.barCount; i++) {
      // A ladder's rungs are pegs, drawn with the ladder below; only a plain bar hangs from a post.
      if (world.barLadder[i]! >= 0) continue;
      const bx = world.barX[i]!;
      const by = world.barY[i]!;
      f.rect(r, bx - 0.02, by, 0.04, BAR_POST_HEIGHT, NEON.barPost, 0.8);
      f.halo(r, bx, by, 0.34, NEON.bar, 0.5);
      f.disc(r, bx, by, BAR_RADIUS, NEON.bar);
    }
    // Salmon ladders: two posts, a pair of pegs at every rung, and one bar that update() sets on its rung.
    for (let i = 0; i < world.ladderCount; i++) {
      const x = world.ladderX[i]!;
      const y0 = world.ladderY0[i]!;
      const rungs = world.ladderRungs[i]!;
      const spacing = world.ladderSpacing[i]!;
      const top = y0 + (rungs - 1) * spacing;
      for (const side of [-1, 1]) {
        const px = x + side * LADDER_HALF_WIDTH;
        f.rect(
          r,
          px - 0.03,
          y0 - LADDER_POST_OVER,
          0.06,
          top - y0 + 2 * LADDER_POST_OVER,
          NEON.barPost,
          0.85,
        );
        for (let k = 0; k < rungs; k++) {
          const py = y0 + k * spacing;
          const inner = side < 0 ? px : px - LADDER_PEG;
          f.rect(r, inner, py - 0.025, LADDER_PEG, 0.05, NEON.barPost, 0.9);
        }
      }
      const halo = f.halo(r, x, y0, 0.34, NEON.bar, 0.5);
      const disc = f.disc(r, x, y0, BAR_RADIUS, NEON.bar);
      disc.width = LADDER_BAR_WIDTH;
      disc.height = LADDER_BAR_HEIGHT;
      this.ladderBars.push({ halo, disc });
    }
    if (world.timed && options.lines !== false) {
      this.drawLine(f, world, world.startLineX, false);
      this.drawLine(f, world, world.finishLineX, true);
    }
  }

  /** A timed course's start line (a green post) or finish line (a checkered post), each with its label. */
  private drawLine(f: ShapeFactory, world: World, x: number, finish: boolean): void {
    const r = this.root;
    const top = world.probeDown(x - 0.01, x + 0.01, 1000, 2000);
    const ground = top > Number.NEGATIVE_INFINITY ? top : 0;
    if (finish) {
      const rows = Math.round(LINE_POST_HEIGHT / FINISH_SQUARE);
      for (let row = 0; row < rows; row++) {
        for (let c = 0; c < 2; c++) {
          const color = (row + c) % 2 === 0 ? NEON.ink : NEON.paper;
          f.rect(
            r,
            x - FINISH_SQUARE + c * FINISH_SQUARE,
            ground + row * FINISH_SQUARE,
            FINISH_SQUARE,
            FINISH_SQUARE,
            color,
          );
        }
      }
      f.rect(r, x - FINISH_SQUARE - 0.03, ground, 0.03, LINE_POST_HEIGHT, NEON.yellow, 0.9);
      f.rect(r, x + FINISH_SQUARE, ground, 0.03, LINE_POST_HEIGHT, NEON.yellow, 0.9);
    } else {
      f.rect(r, x - 0.3, ground, 0.6, LINE_POST_HEIGHT, NEON.startLine, 0.12);
      f.rect(r, x - 0.06, ground, 0.12, LINE_POST_HEIGHT, NEON.startLine, 0.95);
    }
    // A stripe across the ground so the line reads where the feet cross it.
    f.rect(r, x - 0.04, ground - 0.02, 0.08, 0.05, finish ? NEON.yellow : NEON.startLine);
    const label = new BitmapText({
      text: finish ? "FINISH" : "START",
      style: {
        fontFamily: PIXEL_FONT,
        fontSize: LINE_LABEL_FONT_PX,
        fill: 0xffffff,
        stroke: { color: NEON.ink, width: 8 },
      },
    });
    label.tint = finish ? NEON.yellow : NEON.startLine;
    const k = LINE_LABEL_HEIGHT / LINE_LABEL_FONT_PX;
    // The world is scaled with y flipped, so the label flips back to read upright.
    label.scale.set(k, -k);
    label.anchor.set(0.5, 1);
    label.position.set(x, ground + LINE_POST_HEIGHT + 0.16);
    r.addChild(label);
  }

  /**
   * The water's surface breathes (a slow pulse of its glow), and each salmon ladder's bar sits on the
   * player's interpolated grip during a climb, or on the bottom rung when nobody holds it.
   * Without a view (decorative scenes), a held bar defaults to its compiled rung.
   */
  update(feel: RenderFeel, nowMs: number, barId = -1, handY?: number): void {
    const world = this.world;
    for (let i = 0; i < this.ladderBars.length; i++) {
      const bar = this.ladderBars[i]!;
      const onThis = barId >= 0 && world.barLadder[barId] === i;
      const y = onThis ? (handY ?? world.barY[barId]!) : world.ladderY0[i]!;
      bar.halo.position.y = y;
      bar.disc.position.y = y;
    }
    if (this.surfaces.length === 0) return;
    const a =
      feel.waterPulseMs > 0
        ? 0.7 + 0.3 * (0.5 + 0.5 * Math.sin((nowMs / feel.waterPulseMs) * 2 * Math.PI))
        : 0.9;
    for (const s of this.surfaces) s.alpha = a;
  }

  /** Remove everything this renderer created (a course change builds a new one). Shared textures stay. */
  destroy(): void {
    this.root.parent?.removeChild(this.root);
    this.root.destroy({ children: true, texture: false, textureSource: false });
  }
}
